import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  addEntity,
  entityConfig,
  removeEntities,
  setAllEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { VariablesApiService } from '@selfai-platform/pipeline-api';
import {
  GLOBAL_WORKFLOW_ID,
  SavedTreeItem,
  VariableApiError,
  VariableCreateRequest,
  VariableImportItem,
  VariableImportResponse,
  VariableUpdateRequest,
} from '@selfai-platform/pipeline-common';
import { pipe, switchMap } from 'rxjs';
import { OperationStatus } from '../models';
import { extractVariableError, isPendingRequest, mapToVariableImportItem, normalizeVariablesResponse } from '../utils';

const variableEntityConfig = entityConfig({
  entity: {} as SavedTreeItem,
  collection: 'variables',
  selectId: (item: SavedTreeItem) => item.id,
});

export const WorkflowVariablesStore = signalStore(
  withState({
    workflowId: '',
    isLoading: false,
    isDeleting: false,
    isImporting: false,
    isInitialized: false,
    lastError: null as VariableApiError | null,
    lastRequestStatus: null as OperationStatus | null,
    lastImportResult: null as VariableImportResponse[] | null,
  }),
  withEntities(variableEntityConfig),

  withComputed(({ variablesEntities, isLoading, isDeleting, isImporting, lastRequestStatus }) => ({
    isPending: computed(() => isLoading() || isDeleting() || isImporting() || isPendingRequest(lastRequestStatus())),

    variablesAndGroups: computed(() => variablesEntities()),
    variables: computed(() => variablesEntities().filter((item) => 'value' in item)),
    groups: computed(() => variablesEntities().filter((item) => 'variables' in item || !('value' in item))),
    topLevelItems: computed(() => variablesEntities().filter((item) => !item.parentGroupId)),

    getVariablesForExport: computed(() => mapToVariableImportItem(variablesEntities())),
  })),

  withMethods((store) => ({
    setWorkflowId: (newWorkflowId: string) => {
      patchState(store, { workflowId: newWorkflowId });
    },

    setError: (error: VariableApiError) => {
      patchState(store, { lastError: error });
    },

    clearError: () => {
      patchState(store, { lastError: null });
    },

    getItem: (itemId: string): SavedTreeItem | null => {
      return store.variablesEntities().find((v) => v.id === itemId) || null;
    },

    getChildren: (groupId: string): SavedTreeItem[] => {
      return store.variablesEntities().filter((item) => item.parentGroupId === groupId);
    },

    getDescendants: (groupId: string): SavedTreeItem[] => {
      const descendants: SavedTreeItem[] = [];

      function collectDescendants(parentId: string): void {
        const children = store.variablesEntities().filter((item) => item.parentGroupId === parentId);
        children.forEach((child) => {
          descendants.push(child);
          if ('variables' in child) {
            collectDescendants(child.id);
          }
        });
      }

      collectDescendants(groupId);

      return descendants;
    },

    getAncestors: (itemId: string): SavedTreeItem[] => {
      const ancestors: SavedTreeItem[] = [];
      const entityMap = store.variablesEntityMap();
      let currentItem = entityMap[itemId];

      while (currentItem && currentItem.parentGroupId) {
        const parent = entityMap[currentItem.parentGroupId];
        if (parent) {
          ancestors.push(parent);
          currentItem = parent;
        } else {
          break;
        }
      }

      return ancestors;
    },

    clearImportResult: () => {
      patchState(store, { lastImportResult: null });
    },
  })),

  withMethods((store, variablesApi = inject(VariablesApiService), translateService = inject(TranslateService)) => ({
    buildVariablePath: (variable: SavedTreeItem): string => {
      const path = [variable.key];
      const ancestors = store.getAncestors(variable.id);
      ancestors.reverse().forEach((ancestor) => {
        path.unshift(ancestor.key);
      });

      return path.join('.');
    },

    loadVariables: rxMethod<{ includeGlobal?: boolean }>(
      pipe(
        switchMap(({ includeGlobal = false }) => {
          const currentWorkflowId = store.workflowId();

          patchState(store, { isLoading: true });
          store.clearError();

          return variablesApi.getWorkflowVariables(currentWorkflowId, includeGlobal).pipe(
            tapResponse({
              next: (response) => {
                const normalizedItems = normalizeVariablesResponse(response, currentWorkflowId);
                patchState(store, setAllEntities(normalizedItems, variableEntityConfig), {
                  isLoading: false,
                  isInitialized: true,
                });
              },
              error: (httpError) => {
                patchState(store, { isLoading: false, isInitialized: true });
                const error = extractVariableError(httpError, 'workflow.variables.error.load-failed', translateService);
                store.setError(error);
              },
            }),
          );
        }),
      ),
    ),

    createVariable: rxMethod<{
      requestId: string;
      request: Omit<VariableCreateRequest, 'workflowId'>;
      insertAfterId?: string;
    }>(
      pipe(
        switchMap(({ requestId, request, insertAfterId }) => {
          const currentWorkflowId = store.workflowId() === GLOBAL_WORKFLOW_ID ? undefined : store.workflowId();
          store.clearError();
          patchState(store, { lastRequestStatus: { requestId, status: 'pending' } });

          const fullRequest: VariableCreateRequest = {
            ...request,
            workflowId: currentWorkflowId,
          };

          return variablesApi.createVariable(fullRequest).pipe(
            tapResponse({
              next: (newVariable) => {
                const item: SavedTreeItem = {
                  ...newVariable,
                  workflowId: newVariable.isGlobal ? GLOBAL_WORKFLOW_ID : currentWorkflowId,
                  parentGroupId: request.parentId,
                };

                const currentEntities = store.variablesEntities();
                const insertIndex = currentEntities.findIndex((entity) => entity.id === insertAfterId);

                if (insertIndex !== -1) {
                  const newEntities = [
                    ...currentEntities.slice(0, insertIndex + 1),
                    item,
                    ...currentEntities.slice(insertIndex + 1),
                  ];

                  patchState(store, setAllEntities(newEntities, variableEntityConfig), {
                    lastRequestStatus: { requestId, status: 'fulfilled' },
                  });
                } else {
                  patchState(store, addEntity(item, variableEntityConfig), {
                    lastRequestStatus: { requestId, status: 'fulfilled' },
                  });
                }
              },
              error: (httpError) => {
                const error = extractVariableError(
                  httpError,
                  'workflow.variables.error.create-failed',
                  translateService,
                );
                store.setError(error);
                patchState(store, { lastRequestStatus: { requestId, status: 'rejected' } });
              },
            }),
          );
        }),
      ),
    ),

    updateVariable: rxMethod<{ id: string; requestId: string; request: VariableUpdateRequest }>(
      pipe(
        switchMap(({ id, requestId, request }) => {
          store.clearError();
          patchState(store, { lastRequestStatus: { requestId, status: 'pending' } });

          return variablesApi.updateVariable(id, request).pipe(
            tapResponse({
              next: (updatedVariable) => {
                const currentWorkflowId = store.workflowId();
                const item: SavedTreeItem = {
                  ...updatedVariable,
                  workflowId: updatedVariable.isGlobal ? GLOBAL_WORKFLOW_ID : currentWorkflowId,
                  parentGroupId: request.parentId,
                };

                patchState(store, setEntity(item, variableEntityConfig), {
                  lastRequestStatus: { requestId, status: 'fulfilled' },
                });
              },
              error: (httpError) => {
                const error = extractVariableError(
                  httpError,
                  'workflow.variables.error.update-failed',
                  translateService,
                );
                store.setError(error);
                patchState(store, { lastRequestStatus: { requestId, status: 'rejected' } });
              },
            }),
          );
        }),
      ),
    ),

    deleteVariables: rxMethod<string[]>(
      pipe(
        switchMap((variableIds) => {
          const currentWorkflowId = store.workflowId();

          patchState(store, { isDeleting: true });
          store.clearError();

          return variablesApi
            .deleteWorkflowVariables(currentWorkflowId, {
              variables: variableIds,
            })
            .pipe(
              tapResponse({
                next: (deletedIds) => {
                  patchState(store, removeEntities(deletedIds, variableEntityConfig), { isDeleting: false });
                },
                error: (httpError) => {
                  patchState(store, { isDeleting: false });
                  const error = extractVariableError(
                    httpError,
                    'workflow.variables.error.delete-failed',
                    translateService,
                  );
                  store.setError(error);
                },
              }),
            );
        }),
      ),
    ),
  })),

  withMethods((store, variablesApi = inject(VariablesApiService), translateService = inject(TranslateService)) => ({
    importVariables: rxMethod<{ variables: VariableImportItem[]; overwrite: boolean }>(
      pipe(
        switchMap(({ variables, overwrite }) => {
          patchState(store, { isImporting: true });
          store.clearError();
          store.clearImportResult();

          return variablesApi.importVariables(store.workflowId(), variables, overwrite).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { isImporting: false, lastImportResult: response });

                store.loadVariables({ includeGlobal: false });
              },
              error: (httpError) => {
                patchState(store, { isImporting: false });
                const error = extractVariableError(
                  httpError,
                  'workflow.variables.error.import-failed',
                  translateService,
                );
                store.setError(error);
              },
            }),
          );
        }),
      ),
    ),
  })),
);
